/**
 * Auth API module
 * Contains all authentication and user-related API methods
 */

import { apiClient, API_BASE_URL } from './client'

/**
 * Authentication API collection
 */
export const authApi = {
  // ==================== Login Related ====================

  /**
   * 取得 LINE 登入網址
   * Redirecting to this URL will start LINE OAuth flow
   * @returns {string} LINE login URL
   */
  getLineLoginUrl(returnTo = '') {
    const url = new URL(`${API_BASE_URL}/auth/login`)
    if (returnTo) {
      url.searchParams.set('return_to', returnTo)
    }
    return url.toString()
  },

  getTestLoginUrl(returnTo = '') {
    const url = new URL(`${API_BASE_URL}/auth/test-login`)
    if (returnTo) {
      url.searchParams.set('return_to', returnTo)
    }
    return url.toString()
  },

  // ==================== User Information ====================

  /**
   * 取得目前登入使用者的基本資訊
   * @returns {Promise<Object>} User basic information
   * @property {string} userId - User ID
   * @property {string} displayName - Display name
   * @property {string} email - Email
   */
  async getUserInfo() {
    const response = await apiClient.get('/auth/user')
    return response.data
  },

  async getSession() {
    const response = await apiClient.get('/auth/session')
    return response.data
  },

  /**
   * 從 Auth API 取得完整使用者資訊
   * Includes more detailed user data (avatar, bio, etc.)
   * @returns {Promise<Object>} Complete user information
   * @property {string} userId - User ID
   * @property {string} displayName - Display name
   * @property {string} email - Email
   * @property {string} pictureUrl - Avatar URL
   * @property {string} statusMessage - Personal bio
   */
  async getUserInfoFromAuth() {
    const response = await apiClient.get('/Auth/userinfo')
    return response.data
  },

  async createAvatarUploadUrl() {
    const response = await apiClient.post('/Auth/profile/avatar-upload-url')
    return response.data
  },

  async setupProfile(payload) {
    const response = await apiClient.post('/Auth/profile/setup', payload)
    return response.data
  },

  async updateProfile(payload) {
    const response = await apiClient.patch('/Auth/profile', payload)
    return response.data
  },

  // ==================== Token Management ====================

  /**
   * Verify if current token is valid
   * @returns {Promise<boolean>} Whether token is valid
   */
  async verifyToken() {
    try {
      await this.getUserInfo()
      return true
    } catch (error) {
      return false
    }
  },

  /**
   * Logout - Clear local token
   * Note: This is only client-side logout, does not invalidate backend token
   */
  logout() {
    return apiClient.post('/auth/logout')
  },

  // ==================== Permission Check ====================

  /**
   * Check user's comment permission for specific topic
   * @param {number|string} topicId - Topic ID
   * @returns {Promise<Object>} Permission information
   * @property {boolean} canComment - Whether can comment
   * @property {string} userRole - User role (pro/con/audience)
   */
  async checkCommentAuth(topicId) {
    const response = await apiClient.get(`/Auth/comments_auth/${topicId}`)
    return response.data
  }
}

export default authApi
